import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { signOut, getCurrentUser } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import Auth from '../auth/Auth';
import { LanguageDropdown } from './language';
import { isAdminRole } from '../../utils/adminUtils';
import { TextToSpeechToggle } from '../utils/textToSpeech';
import { TextTranslation } from '../utils/textTranslation';
import { listKidProfilesForParentSession } from '../../lib/parentKidProfiles';
import { captureProgramReferralFromSearch } from '../../lib/programReferral';
import NotificationBell from './NotificationBell';
const tinywinsLogo = '/images/TinyWinsLogo.jpeg';
import './Header.css';

interface HeaderProps {
  isAuthenticated: boolean;
  onAuthChange?: (isAuth: boolean) => void;
}

function normalizeOrgWebsiteUrl(raw: unknown): string {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
}

type HeaderOrgRow = { id: string; name: string; website: string | null };

/** Kid id from the current parent route, if the page is scoped to one child. */
function kidIdFromPathname(pathname: string): string | null {
  const patterns = [
    /^\/kid-profile\/([^/]+)/,
    /^\/goal-coach\/([^/]+)/,
    /^\/milestone-tasks\/([^/]+)/,
    /^\/intake-form\/([^/]+)/,
    /^\/daily-diary\/([^/]+)/,
    /^\/team-management\/([^/]+)/,
  ];
  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    const id = match?.[1]?.trim();
    if (id) return id;
  }
  return null;
}

const Header: React.FC<HeaderProps> = ({ isAuthenticated, onAuthChange }) => {
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authInitialState, setAuthInitialState] = useState<'signIn' | 'signUp'>('signIn');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  /** Fallback first kid for menu links when the URL is not kid-scoped. */
  const [defaultKidProfileId, setDefaultKidProfileId] = useState<string | null>(null);
  const [localAuth, setLocalAuth] = useState(isAuthenticated);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [appointmentRequestCount, setAppointmentRequestCount] = useState(0);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [headerOrgs, setHeaderOrgs] = useState<HeaderOrgRow[]>([]);
  const [headerOrgsLoading, setHeaderOrgsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeKidProfileId = useMemo(() => kidIdFromPathname(location.pathname), [location.pathname]);
  const kidProfileId = routeKidProfileId || defaultKidProfileId;
  const isParentsLanding = location.pathname.startsWith('/parents');
  const isParentLanding = location.pathname === '/parent';
  /** Organizations menu only on For Parents experiences (/parents page and 3D /parent tab). */
  const showOrganizationsInHeader = isParentsLanding || isParentLanding;
  const isTherapistLanding = location.pathname.startsWith('/therapist');
  const isLanding = location.pathname === '/' || location.pathname === '/landing-3d' || location.pathname === '/parent' || isParentsLanding || isTherapistLanding;
  const isClinicianLanding = location.pathname === '/';
  const THERAPIST_APPOINTMENT_ROLES = ['THERAPIST', 'CLINICIAN', 'INTERNS', 'ORG_ADMIN', 'SME', 'DOCTOR'] as const;
  const canUseAppointments =
    localAuth &&
    !!userRole &&
    THERAPIST_APPOINTMENT_ROLES.includes(userRole.toUpperCase() as typeof THERAPIST_APPOINTMENT_ROLES[number]);
  const shouldTrackAppointmentRequests =
    localAuth &&
    !!userRole &&
    THERAPIST_APPOINTMENT_ROLES.includes(userRole.toUpperCase() as typeof THERAPIST_APPOINTMENT_ROLES[number]);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const client = useMemo(() => generateClient<Schema>(), []);

  const sortedHeaderOrgs = useMemo(() => {
    return [...headerOrgs].sort((a, b) => a.name.localeCompare(b.name));
  }, [headerOrgs]);

  useEffect(() => {
    if (!showOrganizationsInHeader) {
      setOrgMenuOpen(false);
      return;
    }
    let cancelled = false;
    const loadOrgs = async () => {
      setHeaderOrgsLoading(true);
      try {
        const orgModel = (client.models as Record<string, { list?: (args: object) => Promise<unknown> }>).Organization;
        if (!orgModel?.list) {
          if (!cancelled) setHeaderOrgs([]);
          return;
        }
        let all: HeaderOrgRow[] = [];
        let nextToken: string | null | undefined = undefined;
        do {
          const resp = (await orgModel.list({
            limit: 100,
            nextToken: nextToken ?? undefined,
            authMode: 'apiKey',
          } as object)) as { data?: unknown[]; nextToken?: string | null };
          const rows = Array.isArray(resp?.data) ? resp.data : [];
          for (const o of rows) {
            const row = o as { id?: string; name?: string; website?: string | null };
            if (!row?.id) continue;
            const w = typeof row.website === 'string' && row.website.trim() ? row.website.trim() : null;
            all.push({
              id: row.id,
              name: (row.name || 'Organization').trim() || 'Organization',
              website: w,
            });
          }
          nextToken = resp?.nextToken ?? null;
        } while (nextToken);
        if (!cancelled) setHeaderOrgs(all);
      } catch {
        if (!cancelled) setHeaderOrgs([]);
      } finally {
        if (!cancelled) setHeaderOrgsLoading(false);
      }
    };
    void loadOrgs();
    return () => {
      cancelled = true;
    };
  }, [client, showOrganizationsInHeader]);

  /** e.g. /parents#partner-organizations — open the dropdown; list stays in the header only. */
  useEffect(() => {
    if (!showOrganizationsInHeader || location.hash !== '#partner-organizations') return;
    const t = window.setTimeout(() => {
      setOrgMenuOpen(true);
      window.history.replaceState(null, '', `${location.pathname}${location.search}`);
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash, location.search, showOrganizationsInHeader]);

  useEffect(() => {
    setLocalAuth(isAuthenticated);
  }, [isAuthenticated]);

  // Open Auth modal when URL has ?openAuth=signUp or ?openAuth=signIn (e.g. from therapist Start Free Trial or delete account/data pages)
  useEffect(() => {
    const openAuth = searchParams.get('openAuth');
    if ((openAuth === 'signUp' || openAuth === 'signIn') && !localAuth) {
      captureProgramReferralFromSearch(searchParams);
      setAuthInitialState(openAuth === 'signUp' ? 'signUp' : 'signIn');
      setIsAuthOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, localAuth, setSearchParams]);

  useEffect(() => {
    if (localAuth) {
      fetchKidProfileId();
      fetchUserRole();
    } else {
      setDefaultKidProfileId(null);
      setUserRole(null);
      setAppointmentRequestCount(0);
      setShowUserMenu(false);
    }
  }, [localAuth]);

  const fetchAppointmentRequestCount = async () => {
    if (!shouldTrackAppointmentRequests) {
      setAppointmentRequestCount(0);
      return;
    }
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser?.userId) {
        setAppointmentRequestCount(0);
        return;
      }
      const resp = await client.models.TherapistParentAppointment.list({
        authMode: 'userPool',
        limit: 100,
      });
      const myId = currentUser.userId;
      const role = (userRole || '').toUpperCase();
      const pending = (resp.data || []).filter((a) => {
        const owners = Array.isArray(a.appointmentOwners) ? a.appointmentOwners : [];
        const belongsToMe = a.therapistUserId === myId || a.parentUserId === myId || owners.includes(myId);
        if (!belongsToMe) return false;
        const status = String(a.status || '').toUpperCase();
        if (status !== 'REQUESTED' && status !== 'PENDING') return false;
        const initiatedBy = String(a.initiatedBy || '').toUpperCase();
        if (role === 'PARENT' || role === 'CAREGIVER') {
          if (a.parentUserId !== myId) return false;
          return initiatedBy === 'PARENT' || initiatedBy === 'CAREGIVER' || initiatedBy === '';
        }
        if (a.therapistUserId !== myId) return false;
        return initiatedBy === 'PARENT' || initiatedBy === 'CAREGIVER' || initiatedBy === '';
      });
      setAppointmentRequestCount(pending.length);
    } catch {
      setAppointmentRequestCount(0);
    }
  };

  useEffect(() => {
    void fetchAppointmentRequestCount();
    if (!shouldTrackAppointmentRequests) return;
    const timer = window.setInterval(() => {
      void fetchAppointmentRequestCount();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [shouldTrackAppointmentRequests, userRole]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!showUserMenu && !orgMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
      if (orgMenuOpen && !target.closest('.org-menu-container')) {
        setOrgMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, orgMenuOpen]);

  const closeOrgMenu = useCallback(() => setOrgMenuOpen(false), []);

  const fetchKidProfileId = async () => {
    try {
      const kids = await listKidProfilesForParentSession();
      if (kids.length > 0 && kids[0].id) {
        setDefaultKidProfileId(kids[0].id);
      } else {
        setDefaultKidProfileId(null);
      }
    } catch (error) {
      setDefaultKidProfileId(null);
    }
  };

  const fetchUserRole = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser?.userId) {
        setUserRole(null);
        return;
      }

      // Try apiKey first (public read is enabled), then fallback to userPool.
      let role: string | null = null;
      try {
        const userResponse = await client.models.User.get({ id: currentUser.userId }, { authMode: 'apiKey' });
        role = userResponse.data?.role || null;
      } catch {
        role = null;
      }
      if (!role) {
        try {
          const userResponse = await client.models.User.get({ id: currentUser.userId }, { authMode: 'userPool' });
          role = userResponse.data?.role || null;
        } catch {
          role = null;
        }
      }
      setUserRole(role);
    } catch (error) {
      setUserRole(null);
    }
  };

  const profileMenuItems = [
    {
      label: 'Daily Diary (WIP)',
      href: kidProfileId ? `/daily-diary/${kidProfileId}` : '/daily-diary',
      show: localAuth
    },
    {
      label: 'FAQ',
      href: '/faq',
      show: true
    },
    {
      label: 'Refer a Parent',
      href: '/refer-parent',
      show: localAuth
    },
    {
      label: 'Send Feedback',
      href: '/feedback',
      show: localAuth
    },
    {
      label: 'Support Team (WIP)',
      href: kidProfileId ? `/team-management/${kidProfileId}` : '/team-management',
      show: localAuth
    },
    {
      label: 'My professional profile',
      href: '/org-admin-dashboard?section=my-profile',
      show:
        localAuth &&
        !!userRole &&
        ['THERAPIST', 'CLINICIAN', 'INTERNS', 'ORG_ADMIN', 'SME', 'DOCTOR'].includes(
          userRole.toUpperCase(),
        ),
    },
    {
      label: 'Organization & plan',
      href: '/org-admin-dashboard?section=settings',
      show: localAuth && !!userRole && userRole.toUpperCase() === 'ORG_ADMIN',
    },
    {
      label: 'Settings',
      href: '/profile',
      show: localAuth,
    },
  ];

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!localAuth) {
      navigate('/');
      return;
    }

    // Use cached role — do not await network on click (was ~4s delay).
    const role = (userRole || '').toUpperCase();
    if (role === 'ADMIN' || role === 'SME' || role === 'SUPER_ADMIN') {
      navigate('/admin-dashboard');
    } else if (
      role === 'ORG_ADMIN' ||
      role === 'THERAPIST' ||
      role === 'CLINICIAN' ||
      role === 'INTERNS' ||
      role === 'DOCTOR'
    ) {
      navigate('/org-admin-dashboard');
    } else {
      // Parent / caregiver home = all-kids picker
      navigate('/kid-profile');
    }
  };

  const handleKidProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (routeKidProfileId) {
      navigate(`/kid-profile/${routeKidProfileId}`);
    } else if (kidProfileId) {
      navigate(`/kid-profile/${kidProfileId}`);
    } else {
      navigate('/kid-profile-form');
    }
  };

  const handleSignOut = async () => {
    if (isSigningOut) return;
    
    try {
      setIsSigningOut(true);
      setShowUserMenu(false);
      setLocalAuth(false);
      if (onAuthChange) {
        onAuthChange(false);
      }
      await signOut({ global: true });
      setDefaultKidProfileId(null);
      navigate('/', { replace: true });
    } catch (error) {
      setLocalAuth(true);
      if (onAuthChange) {
        onAuthChange(true);
      }
    } finally {
      setIsSigningOut(false);
    }
  };



  return (
    <header className="header">
      <div className="header-content">
        <Link to="/" className="logo-container" onClick={handleLogoClick}>
          <img 
            src={tinywinsLogo} 
            alt="TinyWins Logo" 
            className="logo-icon" 
            style={{ 
              width: 150, 
              height: 'auto', 
              objectFit: 'contain'
            }} 
          />
        </Link>
        <nav className="nav-links">
          {isClinicianLanding && (
            <>
              <button type="button" className="nav-button header-hide-mobile" onClick={() => scrollToSection('features')}>Features</button>
              <button type="button" className="nav-button header-hide-mobile" onClick={() => scrollToSection('how-it-works')}>How it works</button>
              <button type="button" className="nav-button header-hide-mobile" onClick={() => scrollToSection('book-meeting')}>Schedule</button>
              <Link to="/parents" className="nav-button header-hide-mobile">For Parents</Link>
              {localAuth && (
                <Link to="/therapist-marketplace" className="nav-button header-hide-mobile">
                  Find a therapist
                </Link>
              )}
              <Link to="/therapists" className="nav-button header-hide-mobile">For Therapists</Link>
            </>
          )}
          {isLanding && !isClinicianLanding && (
            <>
              <Link to={isParentsLanding || isParentLanding ? '/' : '/parent'} className="nav-button header-hide-mobile">
                {isParentsLanding || isParentLanding ? 'For Clinicians' : 'For Parents'}
              </Link>
              {!isTherapistLanding && (
                <Link to="/therapists" className="nav-button header-hide-mobile">For Therapists</Link>
              )}
            </>
          )}
          <Link to="/support" className="nav-button header-hide-mobile">Support</Link>

          {showOrganizationsInHeader && (
            <Link to="/organizations" className="org-menu-trigger nav-button">
              Organizations
            </Link>
          )}

          <div className="tts-button-wrapper header-hide-mobile">
            <TextToSpeechToggle className="header-tts-button" />
          </div>
          
          <div className="translation-button-wrapper header-hide-mobile">
            <TextTranslation showLabel={false} />
          </div>
          
          {localAuth && isAdminRole(userRole) && (
            <div className="header-hide-mobile"><LanguageDropdown /></div>
          )}
          {localAuth && !isSigningOut && kidProfileId && !isAdminRole(userRole) && (
            <button onClick={handleKidProfileClick} className="nav-button header-hide-mobile">
              Kids Profile
            </button>
          )}
          {canUseAppointments && (
            <Link to="/book-meeting" className="nav-button header-hide-mobile appointment-nav-button">
              Appointments
              {appointmentRequestCount > 0 ? (
                <span className="appointment-request-badge">
                  {appointmentRequestCount > 99 ? '99+' : appointmentRequestCount}
                </span>
              ) : null}
            </Link>
          )}
          {(!localAuth || isSigningOut) ? (
            <button 
              className="auth-button"
              onClick={() => setIsAuthOpen(true)}
              aria-label="Login"
              disabled={isSigningOut}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className="person-icon"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
          ) : (
            <div className="user-menu-container flex items-center gap-1">
              <NotificationBell />
              <button 
                className="user-menu-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
                aria-label="User menu"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="person-icon"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
              {showUserMenu && (
                <div className="user-menu">
                  {profileMenuItems.filter(item => item.show).map(item => (
                    <Link
                      key={item.label}
                      to={item.href}
                      className="menu-item"
                      onClick={() => setShowUserMenu(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <Link to="/team-list" className="menu-item" onClick={() => setShowUserMenu(false)}>My Teams</Link>
                  <button 
                    onClick={handleSignOut} 
                    className="menu-item sign-out-button"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>
      </div>
      <Auth
        isOpen={isAuthOpen}
        onClose={() => {
          setIsAuthOpen(false);
          setAuthInitialState('signIn');
        }}
        initialAuthState={authInitialState}
      />
    </header>
  );
};

export default Header; 
